import { useGlobalContext } from 'GlobalContext';
import { useCustomerContext } from 'pages/Customer/CustomerContext';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { toast } from 'react-toastify';
import { update } from 'services/customer';
import { onlyDocumentChars } from 'shared/helpers/string';
import { searchViaCep } from 'services/zipCode';
import EditCustomerModal from './EditCustomerModal';
import validateCustomerForm, { IErrors } from './validations';

interface EditCustomerModalProps {
  isOpen: boolean;
  onClose(): void;
}

interface EditCustomerContextData {
  loading: boolean;
  setLoading(b: boolean): void;
  hasFiscalActive: boolean;
  fiscalActiveIdis: string;
  cnpj: string;
  name: string;
  contact_email: string;
  trade_name: string;
  state_registration: string;
  city_registration: string;
  postal_code: string;
  logradouro: string;
  numero: string;
  complemento: string;
  bairro: string;
  telephone: string;
  setCnpj(s: string): void;
  setName(s: string): void;
  setContactEmail(s: string): void;
  setTradeName(s: string): void;
  setState(s: string): void;
  setCity(s: string): void;
  setPostalCode(s: string): void;
  setAddress(s: string): void;
  setNumber(s: string): void;
  setComplement(s: string): void;
  setNeighborhood(s: string): void;
  setPhone(s: string): void;
  invalidCep: boolean;
  errors: IErrors;
  handleSubmit(): void;
}

const Context = createContext<EditCustomerContextData>(
  {} as EditCustomerContextData,
);

export const useEditCustomerModalContext = () => useContext(Context);

const EditCustomerContext: React.FC<EditCustomerModalProps> = ({
  isOpen,
  onClose,
}) => {
  const [loading, setLoading] = useState(false);
  const { customer, apps, loadCustomer } = useCustomerContext();
  const { confirm } = useGlobalContext();

  const appsWithFiscalActive = useMemo(
    () =>
      customer.document
        ? apps.filter(
            (a) =>
              onlyDocumentChars(a.document) ===
                onlyDocumentChars(customer.document) && a.fiscal === 1,
          )
        : [],
    [apps, customer.document],
  );

  const hasFiscalActive = appsWithFiscalActive.length > 0;

  const fiscalActiveIdis = useMemo(
    () => appsWithFiscalActive.map((a) => a.idi).join(', '),
    [appsWithFiscalActive],
  );

  const [cnpj, setCnpj] = useState('');
  const [name, setName] = useState('');
  const [contact_email, setContactEmail] = useState('');
  const [trade_name, setTradeName] = useState('');
  const [state_registration, setState] = useState('');
  const [city_registration, setCity] = useState('');
  const [postal_code, setPostalCode] = useState('');
  const [logradouro, setAddress] = useState('');
  const [numero, setNumber] = useState('');
  const [complemento, setComplement] = useState('');
  const [bairro, setNeighborhood] = useState('');
  const [telephone, setPhone] = useState('');

  const [invalidCep, setInvalidCep] = useState(false);
  const [errors, setErrors] = useState<IErrors>({});

  const handleSubmit = () => {
    const payload = {
      cnpj: onlyDocumentChars(cnpj),
      name: name.trim(),
      contact_email: contact_email.trim(),
      trade_name: trade_name.trim(),
      state_registration: state_registration.trim(),
      city_registration: city_registration.trim(),
      postal_code: postal_code.trim(),
      logradouro: logradouro.trim(),
      numero: numero.trim(),
      bairro: bairro.trim(),
      telephone: telephone.replace(/\D/g, ''),
      id: customer.id,
      complemento: complemento.trim(),
    };

    const err = validateCustomerForm(payload);

    setErrors(err);

    if (Object.values(err).length) return;

    confirm('Atualizar dados do cliente', 'Deseja continuar?', () => {
      setLoading(true);
      update(customer.id, payload)
        .then(() => {
          toast.success('Cliente salvo com sucesso');
          loadCustomer();
          onClose();
        })
        .catch(() => toast.error('Falha ao salvar o cliente'))
        .finally(() => setLoading(false));
    });
  };

  const searchZipCode = useCallback(() => {
    if (!postal_code) {
      setInvalidCep(false);
      return;
    }

    if (postal_code.length < 9) {
      setInvalidCep(false);
      return;
    }

    if (postal_code.length === 9) {
      searchViaCep(postal_code)
        .then(({ data }) => {
          if (data.erro) {
            setInvalidCep(true);
            setCity('');
            setState('');
            return;
          }
          setInvalidCep(false);
          setAddress(data.logradouro);
          setNeighborhood(data.bairro);
          setCity(data.localidade);
          setState(data.uf);
        })
        .catch(() => {
          setInvalidCep(true);
          setCity('');
          setState('');
        });
    }
  }, [postal_code]);

  useEffect(() => {
    searchZipCode();
  }, [searchZipCode]);

  useEffect(() => {
    setCnpj(customer.document);
    setName(customer.name);
    setContactEmail(customer.email);
    if (customer.tradeName) setTradeName(customer.tradeName);
    if (customer.state) setState(customer.state);
    if (customer.city) setCity(customer.city);
    if (customer.zipcode) setPostalCode(customer.zipcode);
    if (customer.address) setAddress(customer.address);
    if (customer.number) setNumber(customer.number);
    if (customer.complement) setComplement(customer.complement);
    if (customer.neighborhood) setNeighborhood(customer.neighborhood);
    setPhone(customer.phone);
    setInvalidCep(false);
    setErrors({});
    setLoading(false);
  }, [customer, isOpen]);

  const data: EditCustomerContextData = {
    loading,
    setLoading,
    hasFiscalActive,
    fiscalActiveIdis,
    cnpj,
    name,
    contact_email,
    trade_name,
    state_registration,
    city_registration,
    postal_code,
    logradouro,
    numero,
    complemento,
    bairro,
    telephone,
    setCnpj,
    setName,
    setContactEmail,
    setTradeName,
    setState,
    setCity,
    setPostalCode,
    setAddress,
    setNumber,
    setComplement,
    setNeighborhood,
    setPhone,
    invalidCep,
    errors,
    handleSubmit,
  };

  return (
    <Context.Provider value={data}>
      <EditCustomerModal isOpen={isOpen} onClose={onClose} />
    </Context.Provider>
  );
};

export default EditCustomerContext;
